import React, { FC, ReactNode, useState } from 'react';
import {
  BasicContainer,
  BodyText,
  BodyTextLarge,
  BodyTextSmall,
  ButtonsWrapper,
  DangerButton,
  DividerWithLabel,
  DivProps,
  FlexCol,
  FlexRow,
  H2Text,
  H4Text,
  PrimaryButton,
} from '@minecraft.atoms';
import Skeleton from 'react-loading-skeleton';
import { CardNumberElement, Elements, CardCvvElement, CardMonthElement, CardYearElement } from '@recurly/react-recurly';
import styled from 'styled-components';
import { getTypographyStyles } from 'minecraft/src/atoms/Typography/utils';
import { Field, Form, Formik, useFormikContext } from 'formik';
import FormikCheckbox from '@minecraft.formik-checkbox';
import Icon from '@minecraft.icon';
import { useTranslation } from '@blocs.i18n';
import { PaywallLocation } from '../enums/PaywallLocation';
import { useAddPaymentMethod } from './useAddPaymentMethods';
import { CARD_TYPES, PAYMENT_METHOD_TYPE, PAYMENT_TYPES } from '../enums/PaymentMethods';
import { AddPaymentMethodProps, RecurlyFormType } from './types';
import { ApplePay } from '../ApplePay';
import { PayPal } from '../PayPal';
import { APPLE_PAY, PAYPAL } from '../constants';
import AuthorizeMessage from '../CreditCardForm/AuthorizeMessage';

export const StyledInput = styled(Field)<{ $error: boolean }>`
  background: ${({ theme }) => theme.designSystem.bgColors.card};
  border: 1px solid
    ${({ theme, $error }) => ($error ? theme.designSystem.borderColors.error : theme.designSystem.borderColors.default)};
  font-size: ${({ theme }) => getTypographyStyles({ theme, fontSize: 'bodyRegular' }).fontSize};
  line-height: 1;
  outline: none;

  &:active {
    border-color: ${({ theme }) => theme.designSystem.borderColors.select};
  }

  &:focus,
  &:focus-visible {
    outline: 4px solid ${({ theme }) => theme.designSystem.borderColors.inputFocus};
  }
`;

export const AddPaymentSkeleton = () => {
  return (
    <FlexCol gap="6" className="cn_w-full">
      <FlexRow gap="4" className="cn_w-full">
        <Skeleton containerClassName="cn_w-full" height="2rem" />
        <Skeleton containerClassName="cn_w-full" height="2rem" />
      </FlexRow>
      <FlexCol gap="2" className="cn_w-full">
        <Skeleton containerClassName="cn_w-full" height="2rem" />
        <Skeleton containerClassName="cn_w-full" height="2rem" />
        <Skeleton containerClassName="cn_w-full" height="2rem" />
        <FlexRow gap="4" wrap="nowrap" className="cn_w-full">
          <Skeleton containerClassName="cn_w-full" height="2rem" />
          <Skeleton containerClassName="cn_w-full" height="2rem" />
        </FlexRow>
        <FlexRow gap="4" wrap="nowrap" alignItems="flex-end" className="cn_w-full">
          <Skeleton containerClassName="cn_w-full" height="2rem" />
          <Skeleton containerClassName="cn_w-full" height="2rem" />
        </FlexRow>
      </FlexCol>
      <Skeleton containerClassName="cn_w-full" height="3rem" />
    </FlexCol>
  );
};

const getIconForCardType = (cardType: string) => {
  if (cardType === 'MasterCard') {
    return <Icon name="master-card" />;
  }

  if (cardType === 'American Express') {
    return <Icon name="amex" />;
  }

  if (cardType === 'Visa') {
    return <Icon name="visa" />;
  }

  return (
    <FlexRow gap="2" wrap="nowrap" alignItems="center">
      <Icon name="master-card" />
      <Icon name="visa" />
      <Icon name="amex" />
    </FlexRow>
  );
};

export type InputProps = {
  type: string;
  name: string;
  label: string;
  hideLabel?: boolean;
  required?: boolean;
  'data-recurly'?: string;
  dataQaId?: string;
  placeholder?: string;
};
export const Input: FC<InputProps> = ({
  name,
  type,
  label,
  hideLabel,
  required,
  'data-recurly': dataRecurly,
  dataQaId,
  placeholder,
}) => {
  const { errors, touched } = useFormikContext();

  return (
    <FlexCol data-qa-id={dataQaId} gap="1" className="cn_w-full">
      {!hideLabel && (
        <BodyText as="label" fontWeight="semibold" htmlFor={name}>
          {label}
          {required && ' *'}
        </BodyText>
      )}
      <StyledInput
        className="cn_w-full cn_atom_p-2 cn_atom_rounded-sm"
        $error={!!errors[name] && touched[name]}
        type={type}
        aria-label={hideLabel && label}
        id={name}
        name={name}
        data-recurly={dataRecurly}
        placeholder={placeholder}
      />
      {errors[name] && touched[name] ? <BodyText color="error">{errors[name]}</BodyText> : null}
    </FlexCol>
  );
};

const StyledRecurly = styled(CardNumberElement)<{ $error?: boolean; $focused?: boolean }>`
  // We have to style the sub-div due to Recurlys iFrame wrapping around the element
  & > div {
    height: 2.5rem;
    padding: 0 0.5rem;
    border: 1px solid
      ${({ theme, $error }) =>
        $error ? theme.designSystem.borderColors.error : theme.designSystem.borderColors.default};
    border-radius: 0.25rem;
    background-color: ${({ theme }) => theme.designSystem.bgColors.card};

    outline: ${({ theme, $focused }) =>
      $focused ? `4px solid ${theme.designSystem.borderColors.inputFocus}` : undefined};
  }
`;

type StyledRecurlyInput = {
  label: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  RecurlyInput: ReactNode;
  EndSlotComponent?: ReactNode;
  inputProps?: { className?: string; dataQaId?: string };
};

export const StyledRecurlyInput: FC<StyledRecurlyInput> = ({
  label,
  name,
  placeholder = '',
  required,
  // The Recurly Element to use (CC, CVV, month, year are all sensitive fields that need an iFrame wrapped)
  RecurlyInput,
  EndSlotComponent,
  inputProps,
}) => {
  const [focused, setFocused] = useState(false);
  const { errors } = useFormikContext();

  const dataQaId = inputProps?.dataQaId;

  return (
    <FlexCol data-qa-id={dataQaId} gap="1" className="cn_w-full">
      <BodyText as="label" fontWeight="semibold" htmlFor={name}>
        {label}
        {required && ' *'}
      </BodyText>
      <FlexRow alignItems="center" wrap="nowrap" className="cn_w-full">
        <StyledRecurly
          className={inputProps?.className}
          as={RecurlyInput}
          $focused={focused}
          $error={!!errors[name]}
          id={name}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            placeholder: {
              content: placeholder,
            },
          }}
        />
        {EndSlotComponent && <div className="cn_atom_pl-2">{EndSlotComponent}</div>}
      </FlexRow>
      {errors[name] ? <BodyText color="error">{errors[name]}</BodyText> : null}
    </FlexCol>
  );
};

const RecurlyForm: FC<
  RecurlyFormType & {
    paywallLocation?: PaywallLocation;
    selectedCard?: AddPaymentMethodProps['selectedCard'];
    setSelectedCard?: AddPaymentMethodProps['setSelectedCard'];
    showAuthorizeMessage?: boolean;
    ctaLabel?: string;
    threeDActionContext?: AddPaymentMethodProps['threeDActionContext'];
  }
> = ({
  addPaymentError,
  allowCancel,
  onCancel,
  onAddPaymentOption,
  addPaymentLoading,
  onUpdatePaymentOption,
  toggleRemovePaymentModal,
  currencyCode,
  countryCode,
  paywallLocation,
  selectedCard,
  setSelectedCard,
  showAuthorizeMessage,
  ctaLabel,
  threeDActionContext,
}) => {
  const { t } = useTranslation();

  const {
    requestRecurlyTokenAndValidate,
    handlePaymentPriorityChange,
    showApplePayButton,
    onClickApplePay,
    showPayPal,
    onClickPayPal,
    formRef,
    formikRef,
  } = useAddPaymentMethod({
    onAddPaymentOption,
    onUpdatePaymentOption,
    setSelectedCard,
    currencyCode,
    countryCode,
  });

  const isApplePaySelected = selectedCard?.paymentType === PAYMENT_TYPES.APPLE_PAY;
  const isPayPalSelected = selectedCard?.paymentType === PAYMENT_TYPES.PAYPAL;
  const hideCreditCardForm = isPayPalSelected || isApplePaySelected;

  const paymentPriority = [];
  if (selectedCard?.isPrimaryPaymentMethod) paymentPriority.push(PAYMENT_METHOD_TYPE.PRIMARY);
  if (selectedCard?.isBackupPaymentMethod) paymentPriority.push(PAYMENT_METHOD_TYPE.BACKUP);

  return (
    <Formik
      key={`${selectedCard?.id}-${paymentPriority.join()}`}
      innerRef={formikRef}
      initialValues={{
        billingInfoId: selectedCard?.id,
        // eslint-disable-next-line camelcase
        first_name: '',
        // eslint-disable-next-line camelcase
        last_name: '',
        // eslint-disable-next-line camelcase
        postal_code: '',
        paymentPriority,
      }}
      onSubmit={(formData, formikHelpers) => requestRecurlyTokenAndValidate(formData, formikHelpers)}
    >
      <Form ref={formRef}>
        <FlexCol gap="6">
          <FlexRow gap="2">
            <BasicContainer borderRadius="sm" borderPlacement="all" padding={2}>
              <FormikCheckbox
                noMargin
                className="cn_m-0"
                label={t('common:billing.card.primary.set')}
                value={PAYMENT_METHOD_TYPE.PRIMARY}
                groupName="paymentPriority"
                onChange={async (e) => {
                  await handlePaymentPriorityChange({
                    billingInfoId: selectedCard?.id,
                    isPrimaryPaymentMethod: e.target.checked,
                  });
                }}
              />
            </BasicContainer>
            <BasicContainer borderRadius="sm" borderPlacement="all" padding={2}>
              <FormikCheckbox
                noMargin
                className="cn_m-0"
                label={t('common:billing.card.backup.set')}
                value={PAYMENT_METHOD_TYPE.BACKUP}
                groupName="paymentPriority"
                onChange={async (e) => {
                  await handlePaymentPriorityChange({
                    billingInfoId: selectedCard?.id,
                    isBackupPaymentMethod: e.target.checked,
                  });
                }}
              />
            </BasicContainer>
          </FlexRow>
          {!hideCreditCardForm && (
            <FlexCol gap="2">
              {!selectedCard?.id && (
                <>
                  <ApplePay showApplePay={showApplePayButton} onClick={onClickApplePay} />
                  <PayPal showPayPal={showPayPal} onClick={onClickPayPal} />
                  {(showApplePayButton || showPayPal) && <DividerWithLabel label={t('common:billing.card.or')} />}
                </>
              )}
              <Input
                type="text"
                name="first_name"
                label={t('common:form.label.firstName')}
                required
                data-recurly="first_name"
                dataQaId="cc-firstname-container"
                placeholder={selectedCard?.firstName}
              />
              <Input
                type="text"
                name="last_name"
                label={t('common:form.label.lastName')}
                required
                data-recurly="last_name"
                dataQaId="cc-lastname-container"
                placeholder={selectedCard?.lastName}
              />
              <FlexRow data-qa-id="cc-number-container" gap="4" wrap="nowrap" alignItems="center">
                <StyledRecurlyInput
                  name="number"
                  label={t('common:form.label.cardNumber')}
                  required
                  RecurlyInput={CardNumberElement}
                  placeholder={selectedCard?.id && `**** **** **** ${selectedCard?.lastFour}`}
                  EndSlotComponent={getIconForCardType(selectedCard?.cardType)}
                />
              </FlexRow>
              <FlexRow data-qa-id="cc-expiration-container" gap="4" wrap="nowrap">
                <StyledRecurlyInput
                  inputProps={{ dataQaId: 'cc-month-expiration-container' }}
                  name="month"
                  label={t('common:form.label.month')}
                  required
                  RecurlyInput={CardMonthElement}
                  placeholder={selectedCard?.month?.toString()}
                />
                <StyledRecurlyInput
                  inputProps={{ dataQaId: 'cc-year-expiration-container' }}
                  name="year"
                  label={t('common:form.label.year')}
                  required
                  RecurlyInput={CardYearElement}
                  placeholder={selectedCard?.year?.toString()}
                />
              </FlexRow>
              <FlexRow gap="4" wrap="nowrap" alignItems="end">
                <StyledRecurlyInput
                  name="cvv"
                  label={t('common:form.label.cvv')}
                  inputProps={{ dataQaId: 'cc-cvv-container' }}
                  required
                  RecurlyInput={CardCvvElement}
                  EndSlotComponent={
                    <FlexRow wrap="nowrap" hidden mdProps={{ hidden: false }}>
                      <Icon name="amex-cvv" width="3.5" height="2" />
                      <Icon name="visa-mc-cvv" width="3.5" height="2" />
                    </FlexRow>
                  }
                />
                <Input
                  type="text"
                  name="postal_code"
                  label={t('common:form.label.zip')}
                  required
                  data-recurly="postal_code"
                  dataQaId="cc-zipcode-container"
                />
              </FlexRow>
            </FlexCol>
          )}
          {isPayPalSelected && (
            <BasicContainer bgColor="card" borderRadius="lg">
              <FlexRow alignItems="center" gap="3">
                <Icon name="paypal" /> <BodyTextLarge fontWeight="semibold">{PAYPAL}</BodyTextLarge>
              </FlexRow>
            </BasicContainer>
          )}
          {isApplePaySelected && (
            <BasicContainer bgColor="card" borderRadius="lg">
              <FlexRow w="full" gap="6" alignItems="center">
                <Icon height={3.4} name={CARD_TYPES.ApplePay} />
                <H4Text fontWeight="semibold">{APPLE_PAY}</H4Text>
              </FlexRow>
              <FlexRow w="full" gap="6" alignItems="center">
                <BodyTextSmall>
                  {' '}
                  {t('talent:billing.membership.expiresOn', {
                    date: `${selectedCard.month}/${selectedCard.year}`,
                  })}
                </BodyTextSmall>
              </FlexRow>
            </BasicContainer>
          )}
          {showAuthorizeMessage && <AuthorizeMessage />}
          <FlexCol className="cn_w-full" justifyContent="flex-end" gap="2" alignItems="flex-end">
            {addPaymentError && !threeDActionContext?.token && <BodyText color="error">{addPaymentError}</BodyText>}
            <ButtonsWrapper className="cn_w-full">
              {allowCancel && (
                <PrimaryButton
                  type="button"
                  isSmall
                  onClick={onCancel}
                  variant="secondary"
                  label={t('common:button.cancel')}
                  disabled={addPaymentLoading || Boolean(threeDActionContext?.token)}
                />
              )}
              {selectedCard?.id && !selectedCard?.isPrimaryPaymentMethod && (
                <DangerButton
                  type="button"
                  label={t('common:button.delete')}
                  onClick={toggleRemovePaymentModal}
                  variant="secondary"
                  isSmall
                  disabled={addPaymentLoading || Boolean(threeDActionContext?.token)}
                />
              )}
              {!isApplePaySelected && !isPayPalSelected && (
                <PrimaryButton
                  type="submit"
                  isSmall
                  label={ctaLabel ?? t('common:paywall.addPaymentMethod.title')}
                  data-unique-id={`add-payment-method-${paywallLocation}`}
                  disabled={addPaymentLoading || Boolean(threeDActionContext?.token)}
                />
              )}
            </ButtonsWrapper>
          </FlexCol>
        </FlexCol>
      </Form>
    </Formik>
  );
};

export const AddPaymentMethod: FC<AddPaymentMethodProps & DivProps> = ({
  onCancel,
  addPaymentError,
  addPaymentLoading,
  allowCancel,
  onAddPaymentOption,
  threeDActionContext,
  currencyCode,
  countryCode,
  paywallLocation,
  selectedCard,
  setSelectedCard,
  onUpdatePaymentOption,
  toggleRemovePaymentModal,
  showAuthorizeMessage,
  omitTitle,
  ctaLabel,
  ...divProps
}) => {
  const { t } = useTranslation();

  return (
    <BasicContainer
      borderRadius="md"
      padding={4}
      borderPlacement="all"
      data-testid="add-payment-container"
      {...divProps}
    >
      <FlexCol gap="4">
        {!omitTitle && (
          <H2Text>
            {selectedCard?.id ? t('common:billing.card.edit') : t('common:paywall.addPaymentMethod.title')}
          </H2Text>
        )}
        <Elements>
          <RecurlyForm
            onCancel={onCancel}
            allowCancel={allowCancel}
            addPaymentError={addPaymentError}
            onAddPaymentOption={onAddPaymentOption}
            addPaymentLoading={addPaymentLoading}
            onUpdatePaymentOption={onUpdatePaymentOption}
            toggleRemovePaymentModal={toggleRemovePaymentModal}
            currencyCode={currencyCode}
            countryCode={countryCode}
            paywallLocation={paywallLocation}
            selectedCard={selectedCard}
            setSelectedCard={setSelectedCard}
            showAuthorizeMessage={showAuthorizeMessage}
            ctaLabel={ctaLabel}
            threeDActionContext={threeDActionContext}
          />
        </Elements>
      </FlexCol>
    </BasicContainer>
  );
};
